import { Flex, Text } from '@chakra-ui/react';
import React, {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispath } from 'shared';
import { Place } from 'entities/place';
import { PlaceCategory } from 'entities/place-category';
// eslint-disable-next-line no-restricted-imports
import { Tabs } from 'shared/ui/Tabs';
import { Helmet } from 'react-helmet-async';
import { PlacesPageActions, usePlacesPageState } from '../../store';
import { PlacesPageMainList } from './main-list';
import { PlacesPageMainHeader } from './main-header';

/**
 * Функция, которая создаёт компонент лейбла для таба
 *
 * @param {string} label Текст
 * @returns {JSX.Element} Компонент
 */
const createPlacesTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem">{label.toLocaleUpperCase()}</Text>
);

/**
 * Функция, которая создаёт компонент контента в табе
 *
 * @param {PortfolioEventModel[]} portfolioEvent Список позиций кондитерской
 * @returns {JSX.Element} Компонент
 */
const createPlacesTabsContentItem = (
    portfolioEvent: Place[],
) => (
    <PlacesPageMainList events={portfolioEvent} />
);

/**
 * Функция, которая создаёт список компонентов для табов
 *
 * @param {PortfolioEventModel[]} places Список позиций кондитерской
 * @param {PortfolioEventCategoryTagModel[]} sweetShopArticleCategoryTags
 * Список тегов для текущей категории
 * @returns {JSX.Element} Параметр items в TabsProps
 */
const createPlacesTabsItems = (
    places: Place[],
    portfolioEventArticleCategory: PlaceCategory[],
) => portfolioEventArticleCategory.map(({
    slug,
    name,
}) => ({
    key: slug,
    labelComponent: createPlacesTabsItemLabel(name),
    contentComponent: createPlacesTabsContentItem(places),
}));

const MemoHeader = memo(PlacesPageMainHeader);

export const PlacesPageMain = () => {
    const dispatch = useAppDispath();
    const { places, categories } = usePlacesPageState();

    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTabKey, setSelectedTabKey] = useState(searchParams.get('tab') || undefined);

    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== selectedTabKey) {
            setSelectedTabKey(urlTab);
        }
    }, [searchParams.get('tab')]);

    useLayoutEffect(() => {
        dispatch(PlacesPageActions.getPlaceCategories());
    }, []);
    useEffect(() => {
        if (!selectedTabKey) {
            setSelectedTabKey(categories.at(0)?.slug);
        }
    }, [categories]);

    useEffect(() => {
        if (selectedTabKey) {
            dispatch(PlacesPageActions.getPlacesByCategory({
                uid: selectedTabKey,
            }));
            if (selectedTabKey !== searchParams.get('tab')) {
                setSearchParams({
                    tab: selectedTabKey,
                });
            }
        }
    }, [selectedTabKey]);

    /**
     * Мемоизированные табы
     */
    const memoTabsItems = useMemo(() => (
        createPlacesTabsItems(
            places,
            categories,
        )
    ), [places, categories]);

    return (
        <Flex
            flexDirection="column"
            gap="2rem"
            padding="2rem"
            maxWidth={{
                base: '425px',
                lg: '1280px',
            }}
            background="white"
            width="100%"
            margin="0 auto"
        >
            <Helmet>
                <title>Партнерские площадки</title>
                <meta name="description" content="Партнерские площадки Corso Catering" />
                <meta property="og:title" content="Партнерские площадки" />
            </Helmet>
            <MemoHeader />
            <Tabs
                value={selectedTabKey}
                onValueChange={(v) => setSelectedTabKey(v)}
                items={memoTabsItems}
            />
        </Flex>
    );
};
